import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { XCircle } from 'lucide-react';
import { PaymentFormData, Transaction, PAYMENT_METHODS } from '@/types/transaction';
import { transactionService } from '@/services/transactionService';

interface AddPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction;
  onPaymentAdded: (paymentData: PaymentFormData) => void;
}

const validationSchema = yup.object({
  metodo: yup.string().oneOf(['EFECTIVO', 'TARJETA', 'TRANSFERENCIA', 'OTRO'], 'Método de pago inválido').required('Método de pago es requerido'),
  monto: yup.number()
    .min(0.01, 'El monto debe ser mayor a 0')
    .required('Monto es requerido'),
  autorizacion: yup.string().default(''),
  notas: yup.string().default('')
});

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  isOpen,
  onClose,
  transaction,
  onPaymentAdded
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue
  } = useForm<PaymentFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      metodo: 'EFECTIVO',
      monto: 0,
      autorizacion: '',
      notas: ''
    }
  });

  const watchedMethod = watch('metodo');
  const watchedAmount = watch('monto');
  
  // Calculate change when payment exceeds balance
  const changeAmount = Math.max(0, (watchedAmount || 0) - transaction.saldo);

  const onSubmit = async (data: PaymentFormData) => {
    try {
      await onPaymentAdded(data);
      reset();
      onClose();
    } catch (error: any) {
      console.error('Error processing payment:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSetFullAmount = () => {
    setValue('monto', transaction.saldo);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Agregar Pago</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              type="button"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          {/* Transaction Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Venta:</span>
                <span className="font-mono">{transaction.numero}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Cliente:</span>
                <span>{transaction.cliente?.name || transaction.customer?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Total:</span>
                <span className="font-semibold">
                  {transactionService.formatCurrency(transaction.total)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Saldo pendiente:</span>
                <span className="font-bold text-red-600">
                  {transactionService.formatCurrency(transaction.saldo)}
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Método de Pago *
              </label>
              <select
                {...register('metodo')}
                className="input w-full"
              >
                {PAYMENT_METHODS.map(method => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
              {errors.metodo && (
                <p className="text-red-600 text-sm mt-1">{errors.metodo.message}</p>
              )}
            </div>

            {/* Amount */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Monto *
                </label>
                <button
                  type="button"
                  onClick={handleSetFullAmount}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Pagar total
                </button>
              </div>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  {...register('monto', { valueAsNumber: true })}
                  className="input w-full"
                  placeholder="0.00"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-gray-500 text-sm">{transaction.moneda}</span>
                </div>
              </div>
              {errors.monto && (
                <p className="text-red-600 text-sm mt-1">{errors.monto.message}</p>
              )}
              {changeAmount > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-md p-2 mt-1">
                  <p className="text-green-700 text-sm font-medium">
                    💰 Cambio a devolver: {transactionService.formatCurrency(changeAmount)}
                  </p>
                </div>
              )}
            </div>

            {/* Authorization (conditional for TARJETA) */}
            {watchedMethod === 'TARJETA' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código de Autorización
                </label>
                <input
                  type="text"
                  {...register('autorizacion')}
                  className="input w-full"
                  placeholder="Código de autorización de la tarjeta"
                />
                {errors.autorizacion && (
                  <p className="text-red-600 text-sm mt-1">{errors.autorizacion.message}</p>
                )}
              </div>
            )}

            {/* Authorization (conditional for TRANSFERENCIA) */}
            {watchedMethod === 'TRANSFERENCIA' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Transferencia
                </label>
                <input
                  type="text"
                  {...register('autorizacion')}
                  className="input w-full"
                  placeholder="Número de referencia de la transferencia"
                />
                {errors.autorizacion && (
                  <p className="text-red-600 text-sm mt-1">{errors.autorizacion.message}</p>
                )}
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas
              </label>
              <textarea
                {...register('notas')}
                className="input w-full"
                rows={3}
                placeholder="Notas adicionales sobre el pago (opcional)"
              />
              {errors.notas && (
                <p className="text-red-600 text-sm mt-1">{errors.notas.message}</p>
              )}
            </div>

            {/* Payment Summary */}
            {watchedAmount > 0 && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Resumen del Pago</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Pago a registrar:</span>
                    <span className="font-semibold">
                      {transactionService.formatCurrency(watchedAmount || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saldo restante:</span>
                    <span className={`font-semibold ${(transaction.saldo - (watchedAmount || 0)) <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {transactionService.formatCurrency(Math.max(0, transaction.saldo - (watchedAmount || 0)))}
                    </span>
                  </div>
                  {changeAmount > 0 && (
                    <div className="flex justify-between">
                      <span>Cambio a devolver:</span>
                      <span className="font-semibold text-orange-600">
                        {transactionService.formatCurrency(changeAmount)}
                      </span>
                    </div>
                  )}
                  {(transaction.saldo - (watchedAmount || 0)) <= 0 && (
                    <div className="text-green-600 font-medium pt-1 border-t border-green-200">
                      ✓ La venta quedará completamente pagada
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="btn-outline flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
                disabled={isSubmitting || watchedAmount <= 0}
              >
                {isSubmitting ? 'Registrando...' : 'Registrar Pago'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPaymentModal;